import { optionalEmail, REQUIRED_MESSAGE, requiredString } from '@wyz/utils';
import { z } from 'zod';
export const QUOTE_DISPLAY_TYPE = {
  COLUMNAR: 'COLUMNAR',
  STANDARD: 'STANDARD',
  LINES: 'LINES',
  UNSET: 'UNSET',
} as const;

export const QUOTE_VIEW_MODE = {
  CLIENT: 'CLIENT',
  POINT_OF_SALE: 'POS',
  UNSET: 'UNSET',
} as const;

export const INACTIVITY_THRESHOLD = {
  TWELVE_MONTHS: 12,
  TWENTY_FOUR_MONTHS: 24,
  THIRTY_SIX_MONTHS: 36,
} as const;

const logoScheme = z
  .object({
    id: z.string(),
    resourcesFormat: z.array(z.object({ url: z.string() })),
  })
  .nullish();
const themeSchema = z.object({
  primaryColor: z.string().nullish(),
  textColorActive: z.string().nullish(),
  textColorInactive: z.string().nullish(),
  primaryTextColor: z.string().nullish(),
  subtitleColor: z.string().nullish(),
  platformBackgroundColor: z.string().nullish(),
  menuBackgroundColor: z.string().nullish(),
  platformLogo: logoScheme,
  automakerLogo: logoScheme,
  banner: logoScheme,
  emailBanner: logoScheme,
});

export const customerThemesSchema = themeSchema.extend({
  criteriaThemes: z.array(
    themeSchema.extend({
      criteria: z
        .array(
          z.object({
            criterion: z.object({ id: z.string() }),
            values: z.array(requiredString).min(1, {
              error: REQUIRED_MESSAGE,
            }),
          }),
        )
        .min(1, REQUIRED_MESSAGE),
    }),
  ),
});

export const customerThemesPayloadSchema = themeSchema.extend({
  criteriaThemes: z.array(
    themeSchema.extend({
      criteria: z.array(
        z.object({ criterionId: z.string(), valueIds: z.array(z.string()) }),
      ),
    }),
  ),
});

export const customerSettingsSchema = z.object({
  maxTyreSearchResult: z.number().positive(),
});
export const featureFlagsSchema = z.object({
  enabledPurchase: z.boolean(),
  enabledRedistribution: z.boolean(),
  enabledQuote: z.boolean(),
  enabledQuoteExpress: z.boolean(),
  enabledQuoteResultDisplayFormat: z.boolean(),
  enabledQuoteResultLinesDisplayFormat: z.boolean(),
  enabledQuoteResultDefaultDisplayFormat: z.boolean(),
  enabledChat: z.boolean(),
  enabledContainer: z.boolean(),
  enabledExtraBonus: z.boolean(),
  enabledDestocking: z.boolean(),
});

export const pluginSchema = z.object({
  enabled: z.boolean(),
  pluginConfigUrl: z.string().nullish(),
});

export const updateTenantFormSchema = z
  .object({
    name: requiredString,
    businessName: z.string().nullish(),
    email: optionalEmail,
    address: z.string().nullish(),
    legacyTenantCodes: z.array(
      z.object({
        code: z.string(),
      }),
    ),
    theme: customerThemesSchema.nullish(),
    settings: z
      .object({
        maxTyreSearchResult: z.number().positive(),
        redistributionMaxDelay: z.number().min(1).nullish(),
      })
      .nullish(),
    features: featureFlagsSchema.nullish(),
    plugin: pluginSchema.nullish(),
    quoteResultDisplayType: z.enum(QUOTE_DISPLAY_TYPE).nullish(),
    quoteResultViewMode: z.enum(QUOTE_VIEW_MODE).nullish(),
    matomoSiteId: z.string().nullish(),
    inactivityThresholdInMonthsBtoB: z
      .number()
      .refine((v) => [12, 24, 36].includes(v))
      .nullish(),
    inactivityThresholdInMonthsBtoC: z
      .number()
      .refine((v) => [12, 24, 36].includes(v))
      .nullish(),
  })
  .refine(
    (data) => {
      if (data.features?.enabledQuoteExpress && !data.features.enabledQuote) {
        return false;
      }
      return true;
    },
    {
      path: ['features.enabledQuote'],
      error: 'app.general_settings.error.quote_express_without_quote',
    },
  )
  .refine(
    (data) => {
      // Destocking is a sub-module of redistribution and cannot outlive it.
      if (
        data.features?.enabledDestocking &&
        !data.features.enabledRedistribution
      ) {
        return false;
      }
      return true;
    },
    {
      path: ['features.enabledDestocking'],
      error: 'app.general_settings.error.destocking_without_redistribution',
    },
  )
  .refine(
    (data) => {
      // The quote result is unreachable without a display mode to render it with.
      if (!data.features?.enabledQuote) {
        return true;
      }

      return (
        data.features.enabledQuoteResultDisplayFormat ||
        data.features.enabledQuoteResultLinesDisplayFormat ||
        data.features.enabledQuoteResultDefaultDisplayFormat
      );
    },
    {
      path: ['features.enabledQuoteResultDefaultDisplayFormat'],
      error: 'app.general_settings.error.quote_without_display_format',
    },
  );

export const updateTenantPayloadSchema = z.object({
  name: z.string().nullish(),
  businessName: z.string().nullish(),
  email: optionalEmail,
  address: z.string().nullish(),
  legacyTenantCodes: z.array(z.string()).nullish(),
  theme: customerThemesPayloadSchema.nullish(),
  settings: z
    .object({
      maxTyreSearchResult: z.number().positive(),
    })
    .nullish(),
  features: featureFlagsSchema.nullish(),
  plugin: pluginSchema.nullish(),
  quoteResultDisplayType: z.string().nullish(),
  quoteResultViewMode: z.string().nullish(),
  matomoSiteId: z.string().nullish(),
  inactivityThresholdInMonthsBtoB: z.number().nullish(),
  inactivityThresholdInMonthsBtoC: z.number().nullish(),
});
